import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../api.js'
import { useCreateOperatorTenant } from '../operator-tenants/queries.js'
import { Button, Input, Toast } from '../ui/index.js'
import styles from './CreateTenantPage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-046` — operator-direct tenant creation.
 *
 * **A second front door onto `DR-037`'s own operation, not a second
 * implementation of it.** `LGH-034`'s onboarding queue exists for the
 * customer who messages WhatsApp first; this exists for the one who calls,
 * emails, or is closed by a sales conversation instead — the founder's own
 * instruction, 2026-09-18.
 *
 * **Field list is the founder's own settled ruling, not a guess**: tenant
 * `Name` only (commercial rules take defaults, edited later in `LGH-039`).
 * Admin `Name` and `Phone number` required — phone is the login credential,
 * console auth is already phone + OTP-over-WhatsApp (`LGH-040`, live-
 * confirmed), no new mechanism. Admin `Email` optional, never required.
 * **No password field, anywhere, ever.**
 *
 * **The one real difference from the queue path, named on screen, not just
 * in this comment**: no inbound message proves the operator has the right
 * number here — the operator is asserting it. Acceptable for an internal,
 * staff-authenticated tool, but a different guarantee than the queue path
 * has, so confirming the phone number a second time is a real step, not
 * decoration — `LGH-046`'s own suggested shape, taken directly.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function CreateTenantPage() {
  const navigate = useNavigate()
  const [tenantName, setTenantName] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminPhone, setAdminPhone] = useState('')
  const [confirmPhone, setConfirmPhone] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const create = useCreateOperatorTenant()

  const phoneTrimmed = adminPhone.trim()
  const phonesMatch = phoneTrimmed !== '' && phoneTrimmed === confirmPhone.trim()
  const canSubmit =
    tenantName.trim() !== '' && adminName.trim() !== '' && phonesMatch && !create.isPending

  const errorMessage = create.isError
    ? create.error instanceof ApiError
      ? create.error.message
      : 'That tenant could not be created.'
    : null

  if (create.isSuccess) {
    return (
      <main className={styles['page']}>
        <h1>Create a tenant</h1>
        <Toast variant="ok">
          {tenantName} was created. {adminName} can now sign in with {adminPhone.trim()} — phone +
          OTP over WhatsApp, the same login every customer console uses.
        </Toast>
        <div className={styles['actions']}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              navigate('/onboarding-requests')
            }}
          >
            Back to onboarding requests
          </Button>
        </div>
      </main>
    )
  }

  return (
    <main className={styles['page']}>
      <h1>Create a tenant</h1>
      <p className={styles['lede']}>
        For a customer who called, emailed, or was closed by a sales conversation — not the WhatsApp
        queue. This creates the account and its first admin in one step.
      </p>

      <form
        className={styles['form']}
        onSubmit={(event) => {
          event.preventDefault()
          if (!canSubmit) return
          create.mutate({
            tenantName: tenantName.trim(),
            adminName: adminName.trim(),
            adminPhone: phoneTrimmed,
            ...(adminEmail.trim() !== '' && { adminEmail: adminEmail.trim() }),
          })
        }}
      >
        <Input
          label="Company name"
          value={tenantName}
          onChange={(event) => {
            setTenantName(event.target.value)
          }}
        />

        <Input
          label="Admin's name"
          value={adminName}
          onChange={(event) => {
            setAdminName(event.target.value)
          }}
        />

        <Input
          label="Admin's phone number"
          type="tel"
          inputMode="tel"
          hint="This becomes their login. They'll sign in with this number and an OTP over WhatsApp."
          placeholder="+971 50 123 4567"
          value={adminPhone}
          onChange={(event) => {
            setAdminPhone(event.target.value)
          }}
        />

        {/*
          `LGH-046`'s own point: no inbound message proves this number here,
          unlike the WhatsApp queue — the operator is asserting it. Re-typing
          it is the confirmation step, not a peer field.
        */}
        <Input
          label="Confirm the phone number"
          type="tel"
          inputMode="tel"
          hint="You're creating login access for this number. Type it again to confirm it's right."
          placeholder="+971 50 123 4567"
          value={confirmPhone}
          onChange={(event) => {
            setConfirmPhone(event.target.value)
          }}
          {...(confirmPhone.trim() !== '' && !phonesMatch
            ? { error: "Doesn't match the number above." }
            : {})}
        />

        <Input
          label="Admin's email (optional)"
          type="email"
          hint="Never required — the phone number alone is enough to sign in."
          value={adminEmail}
          onChange={(event) => {
            setAdminEmail(event.target.value)
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" variant="primary" disabled={!canSubmit} loading={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create tenant'}
          </Button>
        </div>

        {errorMessage !== null && (
          <p role="alert" className={styles['error']}>
            {errorMessage}
          </p>
        )}
      </form>
    </main>
  )
}
